import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Sidebar } from '../components/Sidebar';
import { PageHeader, LoadingSpinner, StatusBadge } from '../components/UIComponents';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { sitesAPI, clientsAPI } from '../utils/api';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { ArrowLeft, Plus, Camera, FileText, Upload, Trash2, Edit, Box, Image } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const SiteOnboardingPage = () => {
  const { siteId } = useParams();
  const navigate = useNavigate();
  const [site, setSite] = useState(null);
  const [client, setClient] = useState(null);
  const [assets, setAssets] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Dialog states
  const [showAddAsset, setShowAddAsset] = useState(false);
  const [showAddContract, setShowAddContract] = useState(false);
  const [showEditContract, setShowEditContract] = useState(false);
  const [showUploadPhoto, setShowUploadPhoto] = useState(false);
  const [editingContract, setEditingContract] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Form states
  const [assetForm, setAssetForm] = useState({
    name: '',
    manufacturer: '',
    model: '',
    serial_number: '',
    capacity_kwh: '',
    power_kw: '',
    installation_date: '',
  });
  
  const [contractForm, setContractForm] = useState({
    contract_name: '',
    contract_number: '',
    start_date: '',
    end_date: '',
    billing_type: 'usage_based',
    rate_per_kwh: '',
    minimum_monthly_kwh: '0',
    fixed_monthly_charge: '0',
    gst_rate: '18',
    gst_included: false,
    billing_cycle_day: '1',
    payment_terms_days: '30',
    currency: 'INR',
    notes: '',
  });
  
  const [photoForm, setPhotoForm] = useState({
    file: null,
    description: '',
    photo_type: 'general',
  });

  useEffect(() => {
    if (siteId) {
      fetchData();
    }
  }, [siteId]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      // Fetch site details
      const siteRes = await axios.get(`${BACKEND_URL}/api/sites/${siteId}`, { headers });
      setSite(siteRes.data);
      
      // Fetch client details
      if (siteRes.data.client_id) {
        const clientRes = await axios.get(`${BACKEND_URL}/api/clients/${siteRes.data.client_id}`, { headers });
        setClient(clientRes.data);
      }
      
      // Fetch assets
      const assetsRes = await axios.get(`${BACKEND_URL}/api/assets?site_id=${siteId}`, { headers });
      setAssets(assetsRes.data);
      
      // Fetch photos
      const photosRes = await axios.get(`${BACKEND_URL}/api/photos?site_id=${siteId}`, { headers });
      setPhotos(photosRes.data);
      
      // Fetch contracts
      const contractsRes = await axios.get(`${BACKEND_URL}/api/contracts?site_id=${siteId}`, { headers });
      setContracts(contractsRes.data);
      
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load site data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddAsset = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${BACKEND_URL}/api/assets`,
        {
          ...assetForm,
          site_id: siteId,
          capacity_kwh: parseFloat(assetForm.capacity_kwh),
          power_kw: parseFloat(assetForm.power_kw),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Equipment added successfully!');
      setShowAddAsset(false);
      setAssetForm({
        name: '', manufacturer: '', model: '', serial_number: '',
        capacity_kwh: '', power_kw: '', installation_date: '',
      });
      fetchData();
    } catch (error) {
      console.error('Failed to add asset:', error);
      toast.error(error.response?.data?.detail || 'Failed to add equipment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddContract = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${BACKEND_URL}/api/contracts`,
        {
          ...contractForm,
          site_id: siteId,
          client_id: site.client_id,
          rate_per_kwh: parseFloat(contractForm.rate_per_kwh) || 0,
          minimum_monthly_kwh: parseFloat(contractForm.minimum_monthly_kwh) || 0,
          fixed_monthly_charge: parseFloat(contractForm.fixed_monthly_charge) || 0,
          gst_rate: parseFloat(contractForm.gst_rate) || 18,
          gst_included: contractForm.gst_included,
          billing_cycle_day: parseInt(contractForm.billing_cycle_day),
          payment_terms_days: parseInt(contractForm.payment_terms_days),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Contract created successfully!');
      setShowAddContract(false);
      resetContractForm();
      fetchData();
    } catch (error) {
      console.error('Failed to create contract:', error);
      toast.error(error.response?.data?.detail || 'Failed to create contract');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditContract = async (e) => {
    e.preventDefault();
    if (!editingContract) return;
    
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${BACKEND_URL}/api/contracts/${editingContract.contract_id}`,
        {
          ...contractForm,
          site_id: siteId,
          client_id: site.client_id,
          rate_per_kwh: parseFloat(contractForm.rate_per_kwh) || 0,
          minimum_monthly_kwh: parseFloat(contractForm.minimum_monthly_kwh) || 0,
          fixed_monthly_charge: parseFloat(contractForm.fixed_monthly_charge) || 0,
          gst_rate: parseFloat(contractForm.gst_rate) || 18,
          gst_included: contractForm.gst_included,
          billing_cycle_day: parseInt(contractForm.billing_cycle_day),
          payment_terms_days: parseInt(contractForm.payment_terms_days),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Contract updated successfully!');
      setShowEditContract(false);
      setEditingContract(null);
      resetContractForm();
      fetchData();
    } catch (error) {
      console.error('Failed to update contract:', error);
      toast.error(error.response?.data?.detail || 'Failed to update contract');
    } finally {
      setSubmitting(false);
    }
  };

  const openEditContractModal = (contract) => {
    setEditingContract(contract);
    setContractForm({
      contract_name: contract.contract_name,
      contract_number: contract.contract_number || '',
      start_date: contract.start_date,
      end_date: contract.end_date,
      billing_type: contract.billing_type || 'usage_based',
      rate_per_kwh: (contract.rate_per_kwh || 0).toString(),
      minimum_monthly_kwh: (contract.minimum_monthly_kwh || 0).toString(),
      fixed_monthly_charge: (contract.fixed_monthly_charge || 0).toString(),
      gst_rate: (contract.gst_rate || 18).toString(),
      gst_included: contract.gst_included || false,
      billing_cycle_day: contract.billing_cycle_day.toString(),
      payment_terms_days: contract.payment_terms_days.toString(),
      currency: contract.currency,
      notes: contract.notes || '',
    });
    setShowEditContract(true);
  };

  const resetContractForm = () => {
    setContractForm({
      contract_name: '', contract_number: '', start_date: '', end_date: '',
      billing_type: 'usage_based', rate_per_kwh: '', minimum_monthly_kwh: '0', 
      fixed_monthly_charge: '0', gst_rate: '18', gst_included: false,
      billing_cycle_day: '1', payment_terms_days: '30', currency: 'INR', notes: '',
    });
  };

  const handleUploadPhoto = async (e) => {
    e.preventDefault();
    if (!photoForm.file) {
      toast.error('Please select a file');
      return;
    }
    
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', photoForm.file);
      formData.append('site_id', siteId);
      formData.append('description', photoForm.description || '');
      formData.append('photo_type', photoForm.photo_type);
      
      await axios.post(
        `${BACKEND_URL}/api/photos/upload`,
        formData,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
      );
      toast.success('Photo uploaded successfully!');
      setShowUploadPhoto(false);
      setPhotoForm({ file: null, description: '', photo_type: 'general' });
      fetchData();
    } catch (error) {
      console.error('Failed to upload photo:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload photo');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteAsset = async (assetId) => {
    if (!window.confirm('Are you sure you want to delete this equipment?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${BACKEND_URL}/api/assets/${assetId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Equipment deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete equipment');
    }
  };

  const handleDeletePhoto = async (photoId) => {
    if (!window.confirm('Are you sure you want to delete this photo?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${BACKEND_URL}/api/photos/${photoId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Photo deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete photo');
    }
  };

  const getPhotoUrl = (photo) => {
    const token = localStorage.getItem('token');
    return `${BACKEND_URL}/api/photos/${photo.photo_id}/download?auth=${token}`;
  };

  if (loading) {
    return (
      <div className="flex">
        <Sidebar />
        <div className="flex-1 ml-0 lg:ml-64 pt-14 lg:pt-0"><LoadingSpinner /></div>
      </div>
    );
  }

  if (!site) {
    return (
      <div className="flex">
        <Sidebar />
        <div className="flex-1 ml-64 p-8">
          <p className="text-red-600">Site not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar />
      <div className="flex-1 ml-0 lg:ml-64 pt-14 lg:pt-0">
        <div className="p-8">
          {/* Breadcrumbs */}
          <Breadcrumbs
            items={[
              { label: 'Sites', to: '/sites' },
              { label: site.name },
            ]}
            onBack={() => navigate('/sites')}
          />
          <div className="flex items-center space-x-4 mb-6">
            <div>
              <h1 className="text-2xl font-bold text-slate-900">{site.name}</h1>
              <p className="text-slate-600">{client?.name} | {site.location}</p>
            </div>
            <StatusBadge status={site.live_status || 'offline'} />
          </div>

          {/* Tabs */}
          <Tabs defaultValue="equipment" className="space-y-6">
            <TabsList className="bg-slate-100 p-1">
              <TabsTrigger value="equipment" className="data-[state=active]:bg-white data-[state=active]:text-slate-900 text-slate-600">
                <Box className="w-4 h-4 mr-2" />
                Equipment
              </TabsTrigger>
              <TabsTrigger value="photos" className="data-[state=active]:bg-white data-[state=active]:text-slate-900 text-slate-600">
                <Image className="w-4 h-4 mr-2" />
                Photos
              </TabsTrigger>
              <TabsTrigger value="contracts" className="data-[state=active]:bg-white data-[state=active]:text-slate-900 text-slate-600">
                <FileText className="w-4 h-4 mr-2" />
                Contracts
              </TabsTrigger>
            </TabsList>

            {/* Equipment Tab (renamed from Assets) */}
            <TabsContent value="equipment">
              <div className="bg-white border border-slate-200 rounded-sm p-6">
                <div className="flex justify-between items-center mb-4">
                  <div>
                    <h2 className="text-lg font-semibold text-slate-900">Site Equipment</h2>
                    <p className="text-sm text-slate-500">Battery systems, inverters, and BMS units at this site</p>
                  </div>
                  <Button onClick={() => setShowAddAsset(true)} className="bg-slate-900">
                    <Plus className="w-4 h-4 mr-2" /> Add Equipment
                  </Button>
                </div>
                
                {assets.length === 0 ? (
                  <div className="text-center py-8 border border-dashed border-slate-300 rounded-sm">
                    <Box className="w-12 h-12 text-slate-300 mx-auto mb-2" />
                    <p className="text-slate-500">No equipment added yet</p>
                    <p className="text-sm text-slate-400">Add battery systems, inverters, or other equipment</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {assets.map((asset) => (
                      <div key={asset.asset_id} className="border border-slate-200 rounded-sm p-4 hover:border-slate-300 transition-colors">
                        <div className="flex justify-between items-start">
                          <div>
                            <h3 className="font-medium text-slate-900">{asset.name}</h3>
                            <p className="text-sm text-slate-600">{asset.manufacturer} - {asset.model}</p>
                            <p className="text-sm text-slate-500">S/N: {asset.serial_number}</p>
                          </div>
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteAsset(asset.asset_id)}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                        <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
                          <div className="bg-slate-50 p-2 rounded">
                            <span className="text-slate-500">Capacity:</span>
                            <span className="ml-1 font-medium">{asset.capacity_kwh} kWh</span>
                          </div>
                          <div className="bg-slate-50 p-2 rounded">
                            <span className="text-slate-500">Power:</span>
                            <span className="ml-1 font-medium">{asset.power_kw} kW</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Photos Tab */}
            <TabsContent value="photos">
              <div className="bg-white border border-slate-200 rounded-sm p-6">
                <div className="flex justify-between items-center mb-4">
                  <div>
                    <h2 className="text-lg font-semibold text-slate-900">Site Photos</h2>
                    <p className="text-sm text-slate-500">Installation, maintenance, and documentation photos</p>
                  </div>
                  <Button onClick={() => setShowUploadPhoto(true)} className="bg-slate-900">
                    <Upload className="w-4 h-4 mr-2" /> Upload Photo
                  </Button>
                </div>
                
                {photos.length === 0 ? (
                  <div className="text-center py-8 border border-dashed border-slate-300 rounded-sm">
                    <Camera className="w-12 h-12 text-slate-300 mx-auto mb-2" />
                    <p className="text-slate-500">No photos uploaded yet</p>
                    <p className="text-sm text-slate-400">Upload installation or maintenance photos</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {photos.map((photo) => (
                      <div key={photo.photo_id} className="relative group border border-slate-200 rounded-sm overflow-hidden">
                        <div className="w-full h-32 bg-slate-100 flex items-center justify-center">
                          <img
                            src={getPhotoUrl(photo)}
                            alt={photo.description || 'Site photo'}
                            className="w-full h-32 object-cover"
                            onError={(e) => { 
                              e.target.style.display = 'none';
                              e.target.parentElement.innerHTML = '<div class="flex items-center justify-center h-full"><svg class="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg></div>';
                            }}
                          />
                        </div>
                        <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeletePhoto(photo.photo_id)}
                            className="text-white hover:text-red-300"
                          >
                            <Trash2 className="w-5 h-5" />
                          </Button>
                        </div>
                        <div className="p-2 bg-white">
                          <p className="font-medium text-sm capitalize text-slate-700">{photo.photo_type}</p>
                          {photo.description && <p className="text-xs text-slate-500 truncate">{photo.description}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Contracts Tab */}
            <TabsContent value="contracts">
              <div className="bg-white border border-slate-200 rounded-sm p-6">
                <div className="flex justify-between items-center mb-4">
                  <div>
                    <h2 className="text-lg font-semibold text-slate-900">Contracts & Pricing</h2>
                    <p className="text-sm text-slate-500">Billing rates and contract terms for this site</p>
                  </div>
                  <Button onClick={() => { resetContractForm(); setShowAddContract(true); }} className="bg-slate-900">
                    <Plus className="w-4 h-4 mr-2" /> Add Contract
                  </Button>
                </div>
                
                {contracts.length === 0 ? (
                  <div className="text-center py-8 border border-dashed border-slate-300 rounded-sm">
                    <FileText className="w-12 h-12 text-slate-300 mx-auto mb-2" />
                    <p className="text-slate-500">No contracts created yet</p>
                    <p className="text-sm text-slate-400">Create a contract to enable billing</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {contracts.map((contract) => (
                      <div key={contract.contract_id} className="border border-slate-200 rounded-sm p-4 hover:border-slate-300 transition-colors">
                        <div className="flex justify-between items-start">
                          <div>
                            <h3 className="font-medium text-slate-900">{contract.contract_name}</h3>
                            {contract.contract_number && (
                              <p className="text-sm text-slate-500">#{contract.contract_number}</p>
                            )}
                          </div>
                          <div className="flex items-center space-x-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openEditContractModal(contract)}
                              className="text-slate-600"
                            >
                              <Edit className="w-4 h-4 mr-1" /> Edit
                            </Button>
                            <StatusBadge status={contract.active ? 'active' : 'archived'} />
                          </div>
                        </div>
                        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div className="bg-slate-50 p-2 rounded">
                            <p className="text-slate-500 text-xs">Rate per kWh</p>
                            <p className="font-medium text-slate-900">{contract.currency} {contract.rate_per_kwh}</p>
                          </div>
                          <div className="bg-slate-50 p-2 rounded">
                            <p className="text-slate-500 text-xs">Min Monthly kWh</p>
                            <p className="font-medium text-slate-900">{contract.minimum_monthly_kwh} kWh</p>
                          </div>
                          <div className="bg-slate-50 p-2 rounded">
                            <p className="text-slate-500 text-xs">Fixed Monthly</p>
                            <p className="font-medium text-slate-900">{contract.currency} {contract.fixed_monthly_charge}</p>
                          </div>
                          <div className="bg-slate-50 p-2 rounded">
                            <p className="text-slate-500 text-xs">Period</p>
                            <p className="font-medium text-slate-900">{new Date(contract.start_date).toLocaleDateString()} - {new Date(contract.end_date).toLocaleDateString()}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Add Equipment Dialog */}
      <Dialog open={showAddAsset} onOpenChange={setShowAddAsset}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Equipment</DialogTitle>
            <DialogDescription>Add battery systems, inverters, or BMS units</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddAsset} className="space-y-4">
            <div>
              <Label>Equipment Name</Label>
              <Input value={assetForm.name} onChange={(e) => setAssetForm({...assetForm, name: e.target.value})} required placeholder="e.g., Battery Pack 1" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Manufacturer</Label>
                <Input value={assetForm.manufacturer} onChange={(e) => setAssetForm({...assetForm, manufacturer: e.target.value})} placeholder="e.g., Tesla" />
              </div>
              <div>
                <Label>Model</Label>
                <Input value={assetForm.model} onChange={(e) => setAssetForm({...assetForm, model: e.target.value})} placeholder="e.g., Powerwall 2" />
              </div>
            </div>
            <div>
              <Label>Serial Number</Label>
              <Input value={assetForm.serial_number} onChange={(e) => setAssetForm({...assetForm, serial_number: e.target.value})} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Capacity (kWh)</Label>
                <Input type="number" value={assetForm.capacity_kwh} onChange={(e) => setAssetForm({...assetForm, capacity_kwh: e.target.value})} required />
              </div>
              <div>
                <Label>Power (kW)</Label>
                <Input type="number" value={assetForm.power_kw} onChange={(e) => setAssetForm({...assetForm, power_kw: e.target.value})} required />
              </div>
            </div>
            <div>
              <Label>Installation Date</Label>
              <Input type="date" value={assetForm.installation_date} onChange={(e) => setAssetForm({...assetForm, installation_date: e.target.value})} />
            </div>
            <div className="flex justify-end space-x-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowAddAsset(false)}>Cancel</Button>
              <Button type="submit" disabled={submitting} className="bg-slate-900">{submitting ? 'Adding...' : 'Add Equipment'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Contract Dialog */}
      <Dialog open={showAddContract} onOpenChange={setShowAddContract}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create Contract</DialogTitle>
            <DialogDescription>Define pricing and billing terms for this site</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddContract} className="space-y-4 max-h-[60vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Contract Name</Label>
                <Input value={contractForm.contract_name} onChange={(e) => setContractForm({...contractForm, contract_name: e.target.value})} required />
              </div>
              <div>
                <Label>Contract Number</Label>
                <Input value={contractForm.contract_number} onChange={(e) => setContractForm({...contractForm, contract_number: e.target.value})} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Date</Label>
                <Input type="date" value={contractForm.start_date} onChange={(e) => setContractForm({...contractForm, start_date: e.target.value})} required />
              </div>
              <div>
                <Label>End Date</Label>
                <Input type="date" value={contractForm.end_date} onChange={(e) => setContractForm({...contractForm, end_date: e.target.value})} required />
              </div>
            </div>
            
            {/* Billing Type Selection */}
            <div className="border-t pt-4">
              <h4 className="font-medium mb-3 text-slate-900">Billing Type</h4>
              <div className="grid grid-cols-2 gap-4">
                <div 
                  className={`p-3 border rounded-sm cursor-pointer transition-all ${contractForm.billing_type === 'usage_based' ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:border-slate-300'}`}
                  onClick={() => setContractForm({...contractForm, billing_type: 'usage_based'})}
                >
                  <p className="font-medium text-sm">Usage-Based</p>
                  <p className="text-xs text-slate-500">Bill based on kWh discharged</p>
                </div>
                <div 
                  className={`p-3 border rounded-sm cursor-pointer transition-all ${contractForm.billing_type === 'fixed_rental' ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:border-slate-300'}`}
                  onClick={() => setContractForm({...contractForm, billing_type: 'fixed_rental'})}
                >
                  <p className="font-medium text-sm">Fixed Rental</p>
                  <p className="text-xs text-slate-500">Flat monthly fee</p>
                </div>
              </div>
            </div>

            <div className="border-t pt-4">
              <h4 className="font-medium mb-3 text-slate-900">Pricing</h4>
              {contractForm.billing_type === 'usage_based' ? (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Rate per kWh (Discharge)</Label>
                      <Input type="number" step="0.01" value={contractForm.rate_per_kwh} onChange={(e) => setContractForm({...contractForm, rate_per_kwh: e.target.value})} required />
                    </div>
                    <div>
                      <Label>Currency</Label>
                      <Select value={contractForm.currency} onValueChange={(v) => setContractForm({...contractForm, currency: v})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="INR">INR</SelectItem>
                          <SelectItem value="USD">USD</SelectItem>
                          <SelectItem value="EUR">EUR</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mt-4">
                    <div>
                      <Label>Minimum Monthly kWh</Label>
                      <Input type="number" value={contractForm.minimum_monthly_kwh} onChange={(e) => setContractForm({...contractForm, minimum_monthly_kwh: e.target.value})} />
                      <p className="text-xs text-slate-500 mt-1">Minimum billable discharge per month</p>
                    </div>
                    <div>
                      <Label>Fixed Monthly Charge</Label>
                      <Input type="number" step="0.01" value={contractForm.fixed_monthly_charge} onChange={(e) => setContractForm({...contractForm, fixed_monthly_charge: e.target.value})} />
                    </div>
                  </div>
                </>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Monthly Rental Amount</Label>
                    <Input type="number" step="0.01" value={contractForm.fixed_monthly_charge} onChange={(e) => setContractForm({...contractForm, fixed_monthly_charge: e.target.value})} required />
                  </div>
                  <div>
                    <Label>Currency</Label>
                    <Select value={contractForm.currency} onValueChange={(v) => setContractForm({...contractForm, currency: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="INR">INR</SelectItem>
                        <SelectItem value="USD">USD</SelectItem>
                        <SelectItem value="EUR">EUR</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
            </div>
            
            {/* GST Section */}
            <div className="border-t pt-4">
              <h4 className="font-medium mb-3 text-slate-900">GST</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>GST Rate (%)</Label>
                  <Input type="number" step="0.1" value={contractForm.gst_rate} onChange={(e) => setContractForm({...contractForm, gst_rate: e.target.value})} />
                </div>
                <div className="flex items-center pt-6">
                  <input 
                    type="checkbox" 
                    id="gst_included" 
                    checked={contractForm.gst_included}
                    onChange={(e) => setContractForm({...contractForm, gst_included: e.target.checked})}
                    className="mr-2"
                  />
                  <Label htmlFor="gst_included" className="cursor-pointer">GST included in rates</Label>
                </div>
              </div>
            </div>

            <div className="border-t pt-4">
              <h4 className="font-medium mb-3 text-slate-900">Billing Terms</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Billing Cycle Day</Label>
                  <Input type="number" min="1" max="28" value={contractForm.billing_cycle_day} onChange={(e) => setContractForm({...contractForm, billing_cycle_day: e.target.value})} />
                </div>
                <div>
                  <Label>Payment Terms (days)</Label>
                  <Input type="number" value={contractForm.payment_terms_days} onChange={(e) => setContractForm({...contractForm, payment_terms_days: e.target.value})} />
                </div>
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea value={contractForm.notes} onChange={(e) => setContractForm({...contractForm, notes: e.target.value})} rows={2} />
            </div>
            <div className="flex justify-end space-x-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowAddContract(false)}>Cancel</Button>
              <Button type="submit" disabled={submitting} className="bg-slate-900">{submitting ? 'Creating...' : 'Create Contract'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Contract Dialog */}
      <Dialog open={showEditContract} onOpenChange={setShowEditContract}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Contract</DialogTitle>
            <DialogDescription>Update pricing and billing terms</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditContract} className="space-y-4 max-h-[60vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Contract Name</Label>
                <Input value={contractForm.contract_name} onChange={(e) => setContractForm({...contractForm, contract_name: e.target.value})} required />
              </div>
              <div>
                <Label>Contract Number</Label>
                <Input value={contractForm.contract_number} onChange={(e) => setContractForm({...contractForm, contract_number: e.target.value})} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Date</Label>
                <Input type="date" value={contractForm.start_date} onChange={(e) => setContractForm({...contractForm, start_date: e.target.value})} required />
              </div>
              <div>
                <Label>End Date</Label>
                <Input type="date" value={contractForm.end_date} onChange={(e) => setContractForm({...contractForm, end_date: e.target.value})} required />
              </div>
            </div>
            
            {/* Billing Type Selection */}
            <div className="border-t pt-4">
              <h4 className="font-medium mb-3 text-slate-900">Billing Type</h4>
              <div className="grid grid-cols-2 gap-4">
                <div 
                  className={`p-3 border rounded-sm cursor-pointer transition-all ${contractForm.billing_type === 'usage_based' ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:border-slate-300'}`}
                  onClick={() => setContractForm({...contractForm, billing_type: 'usage_based'})}
                >
                  <p className="font-medium text-sm">Usage-Based</p>
                  <p className="text-xs text-slate-500">Bill based on kWh discharged</p>
                </div>
                <div 
                  className={`p-3 border rounded-sm cursor-pointer transition-all ${contractForm.billing_type === 'fixed_rental' ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:border-slate-300'}`}
                  onClick={() => setContractForm({...contractForm, billing_type: 'fixed_rental'})}
                >
                  <p className="font-medium text-sm">Fixed Rental</p>
                  <p className="text-xs text-slate-500">Flat monthly fee</p>
                </div>
              </div>
            </div>

            <div className="border-t pt-4">
              <h4 className="font-medium mb-3 text-slate-900">Pricing</h4>
              {contractForm.billing_type === 'usage_based' ? (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Rate per kWh (Discharge)</Label>
                      <Input type="number" step="0.01" value={contractForm.rate_per_kwh} onChange={(e) => setContractForm({...contractForm, rate_per_kwh: e.target.value})} required />
                    </div>
                    <div>
                      <Label>Currency</Label>
                      <Select value={contractForm.currency} onValueChange={(v) => setContractForm({...contractForm, currency: v})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="INR">INR</SelectItem>
                          <SelectItem value="USD">USD</SelectItem>
                          <SelectItem value="EUR">EUR</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mt-4">
                    <div>
                      <Label>Minimum Monthly kWh</Label>
                      <Input type="number" value={contractForm.minimum_monthly_kwh} onChange={(e) => setContractForm({...contractForm, minimum_monthly_kwh: e.target.value})} />
                    </div>
                    <div>
                      <Label>Fixed Monthly Charge</Label>
                      <Input type="number" step="0.01" value={contractForm.fixed_monthly_charge} onChange={(e) => setContractForm({...contractForm, fixed_monthly_charge: e.target.value})} />
                    </div>
                  </div>
                </>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Monthly Rental Amount</Label>
                    <Input type="number" step="0.01" value={contractForm.fixed_monthly_charge} onChange={(e) => setContractForm({...contractForm, fixed_monthly_charge: e.target.value})} required />
                  </div>
                  <div>
                    <Label>Currency</Label>
                    <Select value={contractForm.currency} onValueChange={(v) => setContractForm({...contractForm, currency: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="INR">INR</SelectItem>
                        <SelectItem value="USD">USD</SelectItem>
                        <SelectItem value="EUR">EUR</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
            </div>
            
            {/* GST Section */}
            <div className="border-t pt-4">
              <h4 className="font-medium mb-3 text-slate-900">GST</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>GST Rate (%)</Label>
                  <Input type="number" step="0.1" value={contractForm.gst_rate} onChange={(e) => setContractForm({...contractForm, gst_rate: e.target.value})} />
                </div>
                <div className="flex items-center pt-6">
                  <input 
                    type="checkbox" 
                    id="gst_included_edit" 
                    checked={contractForm.gst_included}
                    onChange={(e) => setContractForm({...contractForm, gst_included: e.target.checked})}
                    className="mr-2"
                  />
                  <Label htmlFor="gst_included_edit" className="cursor-pointer">GST included in rates</Label>
                </div>
              </div>
            </div>

            <div className="border-t pt-4">
              <h4 className="font-medium mb-3 text-slate-900">Billing Terms</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Billing Cycle Day</Label>
                  <Input type="number" min="1" max="28" value={contractForm.billing_cycle_day} onChange={(e) => setContractForm({...contractForm, billing_cycle_day: e.target.value})} />
                </div>
                <div>
                  <Label>Payment Terms (days)</Label>
                  <Input type="number" value={contractForm.payment_terms_days} onChange={(e) => setContractForm({...contractForm, payment_terms_days: e.target.value})} />
                </div>
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea value={contractForm.notes} onChange={(e) => setContractForm({...contractForm, notes: e.target.value})} rows={2} />
            </div>
            <div className="flex justify-end space-x-3 pt-4">
              <Button type="button" variant="outline" onClick={() => { setShowEditContract(false); setEditingContract(null); }}>Cancel</Button>
              <Button type="submit" disabled={submitting} className="bg-slate-900">{submitting ? 'Saving...' : 'Save Changes'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Upload Photo Dialog */}
      <Dialog open={showUploadPhoto} onOpenChange={setShowUploadPhoto}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Upload Photo</DialogTitle>
            <DialogDescription>Add installation or maintenance photos</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUploadPhoto} className="space-y-4">
            <div>
              <Label>Photo</Label>
              <Input
                type="file"
                accept="image/*"
                onChange={(e) => setPhotoForm({...photoForm, file: e.target.files[0]})}
                required
                className="cursor-pointer"
              />
            </div>
            <div>
              <Label>Photo Type</Label>
              <Select value={photoForm.photo_type} onValueChange={(v) => setPhotoForm({...photoForm, photo_type: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="general">General</SelectItem>
                  <SelectItem value="installation">Installation</SelectItem>
                  <SelectItem value="maintenance">Maintenance</SelectItem>
                  <SelectItem value="damage">Damage Report</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Description (optional)</Label>
              <Input value={photoForm.description} onChange={(e) => setPhotoForm({...photoForm, description: e.target.value})} placeholder="Brief description of the photo" />
            </div>
            <div className="flex justify-end space-x-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowUploadPhoto(false)}>Cancel</Button>
              <Button type="submit" disabled={submitting} className="bg-slate-900">{submitting ? 'Uploading...' : 'Upload'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
